'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface EmergencyPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EmergencyPopup({ isOpen, onClose }: EmergencyPopupProps) {
  const [form, setForm] = useState({ name: '', company: '', requirement: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch('/api/emergency-support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('success');
      setTimeout(() => {
        onClose();
        setStatus('idle');
        setForm({ name: '', company: '', requirement: '' });
      }, 2500);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-red-900/80 to-primary px-6 py-5 border-b border-red-500/30">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500/20 border border-red-500/40 rounded-lg flex items-center justify-center">
              <Icon name="PhoneIcon" size={20} className="text-red-400" />
            </div>
            <div>
              <h2 className="font-headline text-xl font-bold text-foreground">Emergency Support</h2>
              <p className="text-xs text-red-400 font-cta tracking-wider">PRIORITY REQUEST — WINGS BHARATH</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {status === 'success' ? (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 bg-green-500/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto">
                <Icon name="CheckCircleIcon" size={32} className="text-green-400" />
              </div>
              <h3 className="font-headline text-xl font-bold text-foreground">Request Sent!</h3>
              <p className="text-muted-foreground text-sm">
                Our team has been notified via email and WhatsApp. We'll reach out to you immediately.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground mb-2">
                Fill in your details and we'll contact you right away.
              </p>

              <div>
                <label className="block text-xs font-cta text-accent tracking-wider mb-1.5">YOUR NAME *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="John Smith"
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-accent transition-colors text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-cta text-accent tracking-wider mb-1.5">COMPANY *</label>
                <input
                  type="text"
                  required
                  value={form.company}
                  onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  placeholder="Airbus / HAL / DRDO..."
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-accent transition-colors text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-cta text-accent tracking-wider mb-1.5">BRIEF REQUIREMENT *</label>
                <textarea
                  required
                  value={form.requirement}
                  onChange={e => setForm(f => ({ ...f, requirement: e.target.value }))}
                  placeholder="e.g. Urgent sheet metal components for A320 program..."
                  rows={3}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-accent transition-colors text-sm resize-none"
                />
              </div>

              {status === 'error' && (
                <p className="text-red-400 text-xs">
                  Failed to send. Please call us directly: <a href="tel:+919900008890" className="underline">+91 9900008890</a>
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 bg-background border border-border text-muted-foreground font-cta text-sm rounded-lg hover:border-accent transition-colors"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-500 text-white font-cta text-sm rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-60"
                >
                  {status === 'loading' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>SENDING...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="PaperAirplaneIcon" size={16} />
                      <span>SEND REQUEST</span>
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-muted-foreground text-center pt-1">
                Or call directly:{' '}
                <a href="tel:+919900008890" className="text-accent hover:underline">+91 9900008890</a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
